/* eslint-disable @typescript-eslint/no-unused-vars */
/* for mobx decorators? */
import { Attachment, Message } from '@progress/kendo-react-conversational-ui';
import EventEmitter from 'eventemitter3';
import jwtDecode from 'jwt-decode';
import { action, computed, observable, runInAction } from 'mobx';
import { persist } from 'mobx-persist';
import Client from 'twilio-chat';
import { Channel } from 'twilio-chat/lib/channel';
import { Member } from 'twilio-chat/lib/member';

import { ChannelStatus } from '../enums/ChannelStatus';
import IChatOptions from '../interfaces/IChatOptions';
import { IJwt } from '../interfaces/IJwt';
import ITwilioMessage, { ChatAction, IAttachment } from '../interfaces/ITwilioMessage';
import chatService from '../lib/chatService';
import { logError, logInfo, logWarning } from '../lib/logging';
import postMessage, { CrossFrameMessageType } from '../lib/postMessage';
import extractErrorMessage from '../lib/utils/extractErrorMessage';
import RootStore from './RootStore';

export interface IChatListener {
  fn: (message: any) => void;
  off: () => void;
}

export interface IChatUser {
  id: any;
  name?: string;
}

export interface IChatAction {
  name: string;
  payload: object;
  action: any;
}

export interface IChatMessage extends Message {
  retryCallback?: () => void;
  index?: number;
}

interface IMemberUpdateContext {
  member: Member;
  updateReasons: string[];
}

const BOT_ROLE_ASSISTANT = 'assistant';
export const DEFAULT_BOT_NAME = 'Sam';
export const DEFAULT_BOT_GREETING = `Hi. I'm {botName}, an AI assistant. Is there anything I can do for you today?`;
const NEW_MESSAGE_MP3 = 'https://onyx-stoat-9610.twil.io/assets/definite-555.mp3';
const CUSTOMER_NAME = 'You';

const defaultOptions: IChatOptions = {
  env: '',
  apiKey: '',
  expanded: undefined,
  bubbleBackgroundColor: undefined,
  width: 0,
  height: 0,
  initialMessage: '',
  logoUrl: '',
  title: '',
  primaryAccentColor: '',
  primaryAccentTextColor: '',
  backgroundColor: '',
};

class ChatStore {
  @persist
  @observable
  public token: string = '';

  @persist
  @observable
  public channelSid: string = '';

  @persist
  @observable
  public botEnabled: boolean = false;

  @persist
  @observable
  public botName: string = DEFAULT_BOT_NAME;

  @persist
  @observable
  public botGreeting: string = DEFAULT_BOT_GREETING;

  @persist
  @observable
  public status: ChannelStatus = ChannelStatus.Unknown;

  @persist
  @observable
  public affiliateKey?: string;

  @observable
  public audioNotification: HTMLAudioElement | null;

  @observable
  public error: Error | null = null;

  @observable
  // tslint:disable-next-line: ban-types
  public errorRetry: Function | undefined = undefined;

  @observable
  public isCustomerBlocked: boolean = false;

  @observable
  public loading: boolean = false;

  @observable
  public typing: boolean = false;

  @observable
  public isTransferring: boolean = false;

  @observable
  public isEnding: boolean = false;

  @persist
  @observable
  public isEscalating: boolean = false;

  @persist
  @observable
  public hasEscalated: boolean = false;

  @persist
  @observable
  public agentConsumptionIndex: number = -1;

  @observable
  public messages: IChatMessage[] = [];

  @observable
  public messageQueue: ITwilioMessage[] = [];

  @observable
  public env: string = '';

  @observable
  public apiKey: string = '';

  @observable
  public initialMessage: string = '';

  @observable
  public options: IChatOptions = defaultOptions;

  @observable
  public user: IChatUser | null = null;

  @computed get messagesWithTyping() {
    if (!this.typing) {
      return this.messages;
    } else {
      const typingMessage: IChatMessage = {
        text: '',
        author: { id: '-1', name: '' },
        timestamp: new Date(),
        typing: true,
      };
      return [...this.messages, typingMessage];
    }
  }

  protected eventEmitter: EventEmitter = new EventEmitter();
  protected channel: Channel | null = null;
  protected client: Client | null = null;
  protected resolution: string | null = null;
  protected rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.audioNotification = new Audio(NEW_MESSAGE_MP3);
  }

  @action
  public async open(options: IChatOptions, user: IChatUser) {
    // if our affiliate key changes we need to reset
    if (options.affiliateKey !== this.affiliateKey) {
      this.switchAffiliates(options.affiliateKey!, options, user);
    }

    try {
      this.loading = true;
      this.options = options;
      this.user = user;

      if (!this.token || this.isTokenExpired(this.token)) {
        // clear chat store by calling this.close()
        this.close();
        const token = await chatService.createToken(this.options.env, user.id);
        runInAction(() => (this.token = token));
      }
      const client = await chatService.createClient(this.token);
      this.client = client;

      // add listener to update token before it expires
      // accounts for long chat sessions, token will expire in a hour
      this.client.on('tokenAboutToExpire', async () => {
        const updatedToken = await chatService.createToken(this.options.env, user.id);
        client.updateToken(updatedToken);
      });

      if (!this.channelSid) {
        const channelResult = await chatService.createChannel(
          this.options.env,
          this.options.apiKey,
          user.id,
          user.name,
          this.options.affiliateKey,
          this.options.sourceTracking,
          this.options.targetWorker,
        );
        runInAction(() => {
          this.channelSid = channelResult.sid;
          this.botEnabled = channelResult.botEnabled;
          this.botName = channelResult.botName || DEFAULT_BOT_NAME;
          this.botGreeting = channelResult.botGreeting || DEFAULT_BOT_GREETING;
        });
      }

      const { initialResponses } = this.options;

      const attachments: IAttachment[] = [];

      if (!this.botEnabled && initialResponses && initialResponses.length > 0) {
        attachments.push({
          contentType: ChatAction.SuggestAction,
          content: { suggestedActions: initialResponses, inputEnabled: true },
          messageId: new Date().toISOString(),
        });
      }

      const initialMessage: IChatMessage = {
        text: this.formatGreeting(),
        author: { id: '-1', name: '' },
        timestamp: new Date(),
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      runInAction(() => {
        this.messages = [initialMessage];
      });

      const channel = await client.getChannelBySid(this.channelSid);

      channel.on('messageAdded', this.onMessageAdd);
      channel.on('typingStarted', () => this.messageTypingStarted());
      channel.on('typingEnded', () => this.messageTypingEnded());
      channel.on('memberJoined', ({ identity }) => this.memberJoined(identity as string));
      channel.on('memberLeft', (context) => this.memberLeft(context));
      // channel.on('removed', () => this.trace(['removed', ...arguments]));
      channel.on('memberUpdated', (context: IMemberUpdateContext) => this.onMemberUpdated(context));
      // channel.on('messageUpdated', () => this.trace(['messageUpdated', ...arguments]));

      const channelMessages = await channel.getMessages();

      // filters out messages with no body to hide sensitive information
      const filteredChannelMessages = channelMessages.items.filter((channelMessage) => channelMessage.body);

      // leverage the messageAdded function to get a kendoMessage from channel messages.
      const kendoMessagesFromChannel = await Promise.all(
        filteredChannelMessages.map(async (item) => await this.messageAdded(item)),
      );
      const filteredMessages = kendoMessagesFromChannel.filter((msg) => msg !== undefined) as IChatMessage[];

      const messages = [initialMessage, ...filteredMessages];
      runInAction(() => {
        this.affiliateKey = this.options.affiliateKey;
        this.messages = messages;
        this.channel = channel;
        this.loading = false;
      });

      // keep widget in-sync with state on a re-open
      if (this.status === ChannelStatus.Waiting) {
        postMessage(CrossFrameMessageType.ChatInitiated);
      } else if (this.status === ChannelStatus.Active) {
        postMessage(CrossFrameMessageType.ChatServed);
      } else if (this.status === ChannelStatus.Transferred) {
        postMessage(CrossFrameMessageType.ChatTransferred);
      } else if (this.status === ChannelStatus.Ended) {
        postMessage(CrossFrameMessageType.ChatEnded);
      }
    } catch (err) {
      // only auto-retry when can't connect ot twilsock
      const errMessage = extractErrorMessage(err);
      if (errMessage === `Error: Can't connect to twilsock` || errMessage === `Can't connect to twilsock`) {
        logWarning(errMessage, { context: { err } });
        this.restart();
        return;
      } else if (errMessage === 'blocked customer') {
        this.isCustomerBlocked = true;
      }

      // other scenarios show error msg
      if (!(err instanceof Error)) {
        this.handleError(new Error(errMessage), this.restart);
        return;
      }
      this.handleError(err, this.restart);
    }
  }

  public async end() {
    if (this.channel) {
      await this.channel
        .sendMessage('Customer has ended the chat', {
          status: 'customer-end',
        })
        .catch((err) => this.handleError(err));
    }
    postMessage(CrossFrameMessageType.ChatEnded);
  }

  @action
  public async sendMessageWithGreeting(message: string, messageId?: string, removeAttachment?: boolean) {
    // this function will send a message with the initial message as a greeting property.

    if (messageId) {
      this.removeAttachment(messageId);
    }

    if (this.status !== ChannelStatus.Active) {
      this.status = ChannelStatus.Waiting;
      postMessage(CrossFrameMessageType.ChatInitiated);
    }

    if (this.channel && message) {
      const status = 'chat-initial-response';

      const placeholderMessage = this.setPlaceholderMessage(message);
      try {
        await this.channel
          .sendMessage(message, {
            id: this.user!.id,
            name: CUSTOMER_NAME,
            status,
            greeting: this.formatGreeting(),
          })
          .catch((err) => {
            throw new Error(err);
          });
      } catch (err) {
        this.handleSendError(placeholderMessage, () => this.retrySendingMessage(placeholderMessage, this.sendMessage));
        return;
      }
    }
  }

  @action
  public close = async () => {
    if (this.channelSid || this.channel) {
      runInAction(() => {
        this.client && this.client.removeAllListeners();
        this.channel && this.channel.removeAllListeners();
        this.token = '';
        this.channelSid = '';
        this.channel = null;
        this.status = ChannelStatus.Unknown;
        this.messages = [];
        this.error = null;
        this.affiliateKey = undefined;
        this.isEscalating = false;
        this.hasEscalated = false;
      });
    }
  };

  @action
  public sendMessage = async (text: string, status?: string) => {
    if (this.status !== ChannelStatus.Active) {
      this.status = ChannelStatus.Waiting;
      postMessage(CrossFrameMessageType.ChatInitiated);
    }

    if (this.channel && (text || status)) {
      // if message is a InitialSuggestedResponse append a 'chat-initial-response' status
      const isInitialSuggestedResponse = this.messages.length === 2 && this.messages[1].status === 'chat-greeting';
      status = isInitialSuggestedResponse ? 'chat-initial-response' : status;

      const placeholderMessage = this.setPlaceholderMessage(text);
      try {
        await this.channel
          .sendMessage(text, {
            id: this.user!.id,
            name: CUSTOMER_NAME,
            status,
          })
          .catch((err) => {
            throw new Error(err);
          });
      } catch (err) {
        this.handleSendError(placeholderMessage, () => this.retrySendingMessage(placeholderMessage, this.sendMessage));
        return;
      }
    }
  };

  @action public removeAttachment = (messageId: string) => {
    const foundMessage = this.messages.filter((msg) => {
      if (msg.timestamp) {
        return msg.timestamp.toISOString() === messageId;
      }
      return false;
    })[0];
    if (foundMessage) {
      foundMessage.attachments = undefined;
    }
  };

  @action
  public sendSimpleMessage = async (message: string, messageId: string, removeAttachment?: boolean) => {
    // remove attachment
    const found = this.messages.filter((msg) => {
      if (msg.timestamp) {
        return msg.timestamp.toISOString() === messageId;
      }
      return false;
    })[0];
    if (found && removeAttachment) {
      found.attachments = undefined;
    }

    if (this.status !== ChannelStatus.Active) {
      this.status = ChannelStatus.Waiting;
    }

    this.sendMessage(message);
  };

  @action
  public setMessagesConsumed = async () => {
    if (this.channel) {
      await this.channel.setAllMessagesConsumed().catch((err) => this.handleError(err));
    }
  };

  @action
  public sendTyping = async () => {
    if (this.channel) {
      await this.channel.typing().catch((err) => this.handleError(err));
    }
  };

  @action
  public restart = async () => {
    this.clearError();
    await this.close();
    await this.reopen();
  };

  @action
  public mute = (muted: boolean) => {
    if (muted) {
      this.audioNotification = null;
    } else {
      this.audioNotification = new Audio(NEW_MESSAGE_MP3);
    }
  };

  public onMessage(fn: (message: IChatMessage) => void): IChatListener {
    this.eventEmitter.addListener('chat-message', fn);
    return {
      fn,
      off: () => {
        this.eventEmitter.removeListener('chat-message', fn);
      },
    };
  }

  public onAction(fn: (message: IChatAction) => void) {
    this.eventEmitter.addListener('chat-action', fn);
    return {
      fn,
      off: () => {
        this.eventEmitter.removeListener('chat-action', fn);
      },
    };
  }

  public onMemberUpdated(memberUpdate: IMemberUpdateContext) {
    if (memberUpdate.updateReasons.includes('lastConsumedMessageIndex')) {
      this.checkAgentConsumption(memberUpdate.member);
    }
  }

  @action
  public checkAgentConsumption(member: Member) {
    this.agentConsumptionIndex = member.lastConsumedMessageIndex;
  }

  @action
  protected memberJoined(identity: string) {
    this.status = ChannelStatus.Active;
    this.isTransferring = this.isEnding = false;
    postMessage(CrossFrameMessageType.ChatServed, btoa(identity));
  }

  @action
  protected memberLeft(memberLeftContext: any) {
    // check when member leaves to ensure that the customer is the only one remaining before closing the chat
    if (memberLeftContext.channel.members.size === 1 && this.isTransferring) {
      this.status = ChannelStatus.Transferred;
      if (this.resolution) {
        postMessage(CrossFrameMessageType.ChatTransferred, this.resolution);
        this.resolution = null;
      }
    } else if (memberLeftContext.channel.members.size === 1 && this.isEnding) {
      this.status = ChannelStatus.Ended;
      if (this.resolution) {
        postMessage(CrossFrameMessageType.ChatEnded, this.resolution);
        this.resolution = null;
      }
    }
  }

  protected onMessageAdd = async (message: ITwilioMessage) => {
    // who is it from
    const isYou = message.attributes && message.attributes.name === CUSTOMER_NAME;

    // handle any GA events
    if (message.attributes && !!message.attributes.GAEventType) {
      return this.handleGAEvent(message.attributes.GAEventType, message.attributes.extraGAEventInfo);
    }

    // hide remote-assist-suggestion messages
    if (message.attributes && message.attributes.type === 'remote-assist-suggestion') {
      return;
    }

    // if we've escalated to an agent and the agent
    // sends a message, we acknowledge it
    if (this.isEscalating && !isYou) {
      runInAction(() => (this.hasEscalated = true));
    }

    // if the agent/bot sends a message, disable the typing
    // indicator
    if (this.typing && !isYou) {
      runInAction(() => (this.typing = false));
    }

    // flip transferring and ending flags
    if (message && message.attributes && message.attributes.actions && message.attributes.actions.length > 0) {
      for (const act of message.attributes.actions) {
        if (act.action === ChatAction.TransferringChat) {
          runInAction(() => (this.isTransferring = true));
        } else if (act.action === ChatAction.EndingChat) {
          runInAction(() => {
            this.isEnding = true;
            this.hasEscalated = true;
          });
        } else if (act.action === ChatAction.EscalatedToAgent) {
          runInAction(() => (this.isEscalating = true));
          return;
        }
      }
    }

    // add message
    this.messageAdded(message);

    // play audio notification only from agent
    if (message.attributes && message.attributes.name !== CUSTOMER_NAME) {
      this.audioNotification && this.audioNotification.play();
    }
  };

  @action
  private async messageAdded(message: ITwilioMessage) {
    this.removePlaceholderMessage(message);

    // if an action payload is large, it will be cut into batches with a count and batch index
    if (message.attributes && !!message.attributes.count && message.attributes.batch !== undefined) {
      return this.queueMessage(message);
    }

    // convert to our component format
    const msg = await this.twilioMessageToKendoMessage(message);

    // if we have text or an attachment, show it
    if (msg.text || (msg.attachments && msg.attachments.length)) {
      this.messages.push(msg);
    }

    if (message && message.attributes && message.attributes.actions && message.attributes.actions.length > 0) {
      for (const act of message.attributes.actions) {
        this.eventEmitter.emit('chat-action', {
          name: message.attributes.name,
          payload: act.payload,
          action: act.action,
        } as IChatAction);
      }
    } else if (message && message.body) {
      this.eventEmitter.emit('chat-message', msg as IChatMessage);
    }

    logInfo('messages', { context: { messages: this.messages } });

    return msg;
  }

  @action
  private async reopen() {
    await this.open(this.options, this.user!);
  }

  @action
  private messageTypingStarted() {
    this.typing = true;
  }

  @action
  private messageTypingEnded() {
    this.typing = false;
  }

  @action
  private handleError(error: Error, retry?: () => void) {
    this.error = error;
    this.errorRetry = retry;
    logError(error.message, { context: { error } });
  }

  private async getKendoAttachmentFromTwilioMessage(message: ITwilioMessage): Promise<IAttachment | undefined> {
    if (message.type !== 'media' || !message.media) {
      return undefined;
    }

    try {
      const mediaUrl = await message.media.getContentUrl();

      if (message.attributes && message.attributes.action === ChatAction.ShowTimeSlotsMedia) {
        const mediaResponse = await fetch(mediaUrl);
        const reader = mediaResponse.body!.getReader();

        const readableStream = new ReadableStream({
          start(controller) {
            // The following function handles each data chunk
            function push() {
              // "done" is a Boolean and value a "Uint8Array"
              reader.read().then(({ done, value }) => {
                // If there is no more data to read
                if (done) {
                  controller.close();
                  return;
                }
                // Get the data and send it to the browser via the controller
                controller.enqueue(value);
                push();
              });
            }
            push();
          },
        });

        const mediaContent = await new Response(readableStream, {
          headers: { 'Content-Type': 'text/html' },
        }).text();

        const parsedMedia = JSON.parse(mediaContent);

        const attachment: IAttachment = {
          contentType: (message.attributes && message.attributes.action) || '',
          content: parsedMedia,
          messageId: message.timestamp.toISOString(),
        };
        return attachment;
      } else if (message.media.contentType.includes('image')) {
        const attachment: IAttachment = {
          contentType: ChatAction.ShowImage,
          content: { imgUrl: mediaUrl },
          messageId: message.timestamp.toISOString(),
        };
        return attachment;
      }
    } catch (err) {
      logError('error fetching media file', { context: { err } });
    }
    return undefined;
  }

  private async twilioMessageToKendoMessage(message: ITwilioMessage): Promise<IChatMessage> {
    let name = message.author;
    if (message.attributes && message.attributes.name) {
      name = message.attributes.name;
    }
    let id = message.author;
    if (name.toLowerCase() === BOT_ROLE_ASSISTANT) {
      id = BOT_ROLE_ASSISTANT;
      name = this.botName;
    }
    const actionAttachments: Attachment[] = [];

    if (message && message.attributes && message.attributes.actions && message.attributes.actions.length > 0) {
      const actions = message.attributes.actions;

      for (const twilAction of actions) {
        const attachment: IAttachment = {
          contentType: twilAction.action,
          content: twilAction.payload,
          messageId: message.timestamp.toISOString(),
        };
        actionAttachments.push(attachment);
      }
    }

    if (message.type === 'media' && message.media) {
      const attachment = await this.getKendoAttachmentFromTwilioMessage(message);
      attachment && actionAttachments.push(attachment);
    }

    const status = message.attributes ? message.attributes.status : undefined;

    return {
      text: message.body,
      author: { id, name },
      timestamp: message.timestamp,
      attachments: actionAttachments.length > 0 ? actionAttachments : undefined,
      index: message.index,
      status,
    };
  }

  private queueMessage(message: ITwilioMessage) {
    if (!message.attributes) {
      return;
    }

    // queue the message
    this.messageQueue.push(message);

    // once the queue reaches the batch size count
    if (this.messageQueue.length === message.attributes.count) {
      // build message from queue
      const msgFromQueue = this.buildMessageFromQueue(this.messageQueue);
      if (msgFromQueue) {
        // emit message
        this.messages.push(msgFromQueue);
        const { attachments } = msgFromQueue;
        this.eventEmitter.emit('chat-action', {
          name: msgFromQueue.author.name,
          payload: attachments && attachments.length > 0 ? attachments[0].content : undefined,
          action: attachments && attachments.length > 0 ? attachments[0].contentType : undefined,
        } as IChatAction);

        logInfo('messages', { context: { messages: this.messages } });
        return msgFromQueue;
      }
    }
    return;
  }

  private buildMessageFromQueue(messages: ITwilioMessage[]) {
    if (messages.length === 0) {
      return;
    }

    let mergedBody = '';
    let messageIndex = 0;
    const mergedPayload: any[] = [];

    // sort by batch index the messages as they may have been sent/arrived not in order
    const sorted = messages.sort((a, b) => (a.attributes!.batch! > b.attributes!.batch! ? 1 : -1));

    sorted.forEach((message) => {
      // the real message body should only be in the first batch.
      if (message.attributes!.batch! === 0) {
        mergedBody += message.body;
      }

      // merge the payloads
      message.attributes && message.attributes.actions && mergedPayload.push(message.attributes.actions[0].payload);
      if (message.index > messageIndex) {
        messageIndex = message.index;
      }
    });

    let stringifiedPayload = '';
    for (const batch of mergedPayload) {
      stringifiedPayload += batch;
    }

    const payload = JSON.parse(stringifiedPayload);

    const attachment: IAttachment = {
      contentType:
        messages[0].attributes && messages[0].attributes.actions ? messages[0].attributes.actions[0].action : '',
      content: payload,
      messageId: messages[0].timestamp.toISOString(),
    };

    const mergedMessage: IChatMessage = {
      text: mergedBody,
      author: { id: messages[0].author, name: messages[0].attributes!.name },
      timestamp: messages[0].timestamp,
      attachments: [attachment],
      index: messageIndex,
    };

    this.messageQueue = [];

    return mergedMessage;
  }

  // we want to set a placeholder message (because the msg only displays when twilio has received the msg)
  private setPlaceholderMessage = (text: string) => {
    const placeholderMessage = {
      author: {
        id: this.user!.id,
        name: CUSTOMER_NAME,
      },
      status: 'pending',
      text,
      timestamp: new Date(),
      selectionIndex: this.messages.length - 1,
    };
    if (text) {
      this.messages.push(placeholderMessage);
    }
    return placeholderMessage;
  };

  private clearError = () => {
    this.error = null;
    this.errorRetry = undefined;
  };

  private removePlaceholderMessage = (twilioMessage: ITwilioMessage) => {
    this.messages = this.messages.filter((localMsg: IChatMessage, index: number) => {
      if (localMsg.status === 'pending' && localMsg.text === twilioMessage.body) {
        return false;
      }
      return true;
    });
  };

  private removeUndeliveredMessage = (placeholderMessage: IChatMessage) => {
    this.messages = this.messages.filter((localMsg: IChatMessage) => {
      // the minus one accounts for the the greeting msg
      if (localMsg.status === 'undelivered' && localMsg.timestamp === placeholderMessage.timestamp) {
        return false;
      }
      return true;
    });
  };

  private replacePendingWithUndelivered = (placeholderMessage: IChatMessage, undeliveredMessage: IChatMessage) => {
    const index = this.messages.map((msg) => msg.timestamp).indexOf(placeholderMessage.timestamp);
    this.messages[index] = undeliveredMessage;
  };

  private handleSendError = (placeholderMessage: IChatMessage, retry: () => void) => {
    const undeliveredMessage = { ...placeholderMessage };
    undeliveredMessage.retryCallback = retry;
    undeliveredMessage.status = 'undelivered';
    this.replacePendingWithUndelivered(placeholderMessage, undeliveredMessage);
  };

  private retrySendingMessage = (placeholderMessage: IChatMessage, retryCallback: (text: string) => void) => {
    this.removeUndeliveredMessage(placeholderMessage);
    retryCallback(placeholderMessage.text!);
  };

  private handleGAEvent = (eventType: CrossFrameMessageType, extraGAEventInfo) => {
    if (eventType === CrossFrameMessageType.ChatClosed) {
      this.resolution = extraGAEventInfo as string;
    } else {
      postMessage(eventType, extraGAEventInfo);
    }
  };

  private isTokenExpired = (token: string) => {
    const decoded: IJwt = jwtDecode(token);
    const currentSecondsElapsed = Math.floor(new Date().getTime() / 1000);
    if (currentSecondsElapsed < decoded.exp) {
      return false;
    }
    return true;
  };

  private formatGreeting(): string {
    return this.botEnabled ? this.botGreeting.replace(/{botName}/i, this.botName) : this.options.initialMessage;
  }

  @action
  private switchAffiliates(affiliateKey: string, options: IChatOptions, user: IChatUser) {
    // if we're changing affiliates and we have a channel sid we need to
    // clean-up the old channel
    if (this.channelSid) {
      logInfo(`Changing affiliate: to=${affiliateKey} from=${this.affiliateKey}`);

      // we don't want to block the new channel connection so we
      // grab the old channel and token and do it in the background
      // and ignore errors
      const token = this.token;
      const channelSid = this.channelSid;
      let channel: Channel | null = this.channel;
      setTimeout(async () => {
        try {
          if (!channel) {
            if (token && !this.isTokenExpired(token)) {
              const client = await chatService.createClient(token);
              channel = await client.getChannelBySid(channelSid);
            }
          }
          if (channel && this.status === ChannelStatus.Active) {
            await channel.sendMessage('Customer has ended the chat', {
              status: 'customer-end',
            });
          }
        } catch (err) {
          // ignore
        }
      });

      // cleanup and continue
      runInAction(() => {
        channel && channel.removeAllListeners();
        this.token = '';
        this.channelSid = '';
        this.channel = null;
        this.status = ChannelStatus.Unknown;
        this.messages = [];
        this.error = null;
        this.affiliateKey = affiliateKey;
      });
    }
  }
}

export default ChatStore;
